/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, ReactNode } from 'react';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/common/Toast';

type ShowToastFn = (
  message: string,
  type?: 'success' | 'error' | 'info' | 'warning',
  duration?: number,
  action?: { label: string; onClick: () => void }
) => string;

interface ToastContextValue {
  showToast: ShowToastFn;
  success: (message: string, duration?: number) => string;
  error: (message: string, duration?: number) => string;
  warning: (message: string, duration?: number) => string;
  info: (message: string, duration?: number) => string;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * Global toast feedback provider. Replaces native alert() calls across the app
 * with consistent, dismissible, theme-aware notifications.
 */
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const toast = useToast();

  const value: ToastContextValue = {
    showToast: toast.showToast,
    success: toast.success,
    error: toast.error,
    warning: toast.warning,
    info: toast.info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer messages={toast.toasts} onClose={toast.closeToast} />
    </ToastContext.Provider>
  );
};

export const useAppToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useAppToast must be used within a ToastProvider');
  }
  return ctx;
};

export default ToastContext;
